import React, { useState, useMemo, useRef } from 'react';
import { findSkinImageLoose } from './skinIndex';

export default function GroupSellModal({ items, settings, skinsData, livePrices, onConfirm, onClose }) {
  // items: [{ row, realIndex }, ...]

  const sites = settings?.sites || [];
  const [siteFee, setSiteFee] = useState(sites[0] || null);

  const [sellPrices, setSellPrices] = useState(
    () => Object.fromEntries(items.map((_, i) => [i, '']))
  );
  const [distributeInput, setDistributeInput] = useState('');
  const distributeRef = useRef(null);

  const feeRate = (siteFee?.salesFee || 0) + (siteFee?.withdrawalFee || 0);

  const totalBuy = useMemo(
    () => items.reduce((sum, { row }) => sum + (parseFloat(row.buyPrice) || 0), 0),
    [items]
  );

  const totalSell = useMemo(
    () => Object.values(sellPrices).reduce((sum, v) => sum + (parseFloat(v) || 0), 0),
    [sellPrices]
  );

  // Net = sell after fees - buy
  const totalNet    = totalSell * (1 - feeRate) - totalBuy;
  const totalProfit = totalNet;
  const profC = settings?.profitTextColor || '#4ade80';
  const lossC = settings?.lossTextColor   || '#f87171';

  const handleSell = (i, val) =>
    setSellPrices(prev => ({ ...prev, [i]: val }));

  const distributeTotal = () => {
    const t = parseFloat(distributeInput);
    if (isNaN(t) || t <= 0) return;
    if (totalBuy === 0) {
      // Equal split when no buy prices set
      const each = (t / items.length).toFixed(2);
      setSellPrices(Object.fromEntries(items.map((_, i) => [i, each])));
    } else {
      setSellPrices(
        Object.fromEntries(
          items.map(({ row }, i) => {
            const buy  = parseFloat(row.buyPrice) || 0;
            const share = totalBuy > 0 ? (buy / totalBuy) * t : t / items.length;
            return [i, share.toFixed(2)];
          })
        )
      );
    }
  };

  const handleConfirm = () => {
    const results = items.map(({ row, realIndex }, i) => {
      const sell   = parseFloat(sellPrices[i]) || 0;
      const buy    = parseFloat(row.buyPrice)  || 0;
      const net    = sell * (1 - feeRate) - buy;
      return { realIndex, sellPrice: sell.toFixed(2), profit: net.toFixed(2), siteFee };
    });
    onConfirm(results);
  };

  const allFilled = items.every((_, i) => {
    const v = parseFloat(sellPrices[i]);
    return !isNaN(v) && sellPrices[i].trim() !== '';
  });

  const roiPct = totalBuy > 0 ? ((totalProfit / totalBuy) * 100) : null;

  return (
    <div className="modal-backdrop" onClick={onClose}
      style={{ alignItems: 'flex-start', paddingTop: 36, paddingBottom: 36 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#0b1220', border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 20, width: 'min(700px, 100%)', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 24px 70px rgba(0,0,0,0.65)',
      }}>

        {/* ── Header ─────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.09rem', fontWeight: 700, color: '#fff' }}>
              Group Sale — {items.length} item{items.length !== 1 ? 's' : ''}
            </div>
            <div style={{ fontSize: '0.92rem', color: 'var(--text-dim)', marginTop: 3 }}>
              Enter a sell price for each item, or distribute a total across all
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* ── Distribute shortcut ─────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '12px 22px', borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0,
          background: 'rgba(255,255,255,0.015)' }}>
          {sites.length > 0 && (
            <>
              <span style={{ fontSize: '0.95rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Selling on:</span>
              <select
                value={siteFee?.value || ''}
                onChange={e => setSiteFee(sites.find(s => s.value === e.target.value) || null)}
                style={{ padding: '4px 8px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(255,255,255,0.07)', color: '#fff', fontSize: '0.95rem',
                  fontFamily: 'var(--font-body)', outline: 'none' }}
              >
                {sites.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              {feeRate > 0 && (
                <span style={{ fontSize: '0.92rem', color: 'var(--text-dim)' }}>
                  fee: {(feeRate * 100).toFixed(2)}%
                </span>
              )}
              <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.10)' }} />
            </>
          )}
          <span style={{ fontSize: '0.95rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            Distribute total:
          </span>
          <input
            ref={distributeRef}
            type="number" min="0" step="0.01"
            value={distributeInput}
            onChange={e => setDistributeInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && distributeTotal()}
            placeholder="e.g. 37.45"
            style={{
              width: 110, padding: '5px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.14)',
              background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: '0.97rem',
              fontFamily: 'var(--font-body)', outline: 'none',
            }}
          />
          <button onClick={distributeTotal} style={{
            padding: '5px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.07)', color: 'var(--text-muted)',
            fontSize: '0.94rem', fontWeight: 600, cursor: 'pointer',
          }}>
            Split by buy price
          </button>
          <span style={{ fontSize: '0.91rem', color: 'var(--text-dim)', marginLeft: 'auto', flexShrink: 0 }}>
            Total buy: <strong style={{ color: 'var(--text)' }}>${totalBuy.toFixed(2)}</strong>
          </span>
        </div>

        {/* ── Item rows ──────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 22px' }}>
          {items.map(({ row }, i) => {
            const buy    = parseFloat(row.buyPrice) || 0;
            const sell   = parseFloat(sellPrices[i]) || 0;
            const net    = sell * (1 - feeRate);
            const profit = sell > 0 ? net - buy : null;
            const hasSell = sellPrices[i].trim() !== '' && !isNaN(parseFloat(sellPrices[i]));
            const imgUrl  = findSkinImg(skinsData, row.skin?.value, row.condition?.value);

            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 0',
                borderBottom: i < items.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
              }}>
                {/* Thumbnail */}
                <div style={{ width: 56, height: 38, flexShrink: 0,
                  background: 'rgba(255,255,255,0.04)', borderRadius: 6,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {imgUrl
                    ? <img src={imgUrl} alt="" style={{ width: 56, height: 38, objectFit: 'contain' }} />
                    : null
                  }
                </div>

                {/* Name + condition */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.98rem', fontWeight: 600, color: '#fff',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.skin?.value}
                  </div>
                  {row.condition?.value && (
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-dim)', marginTop: 1 }}>
                      {row.condition.value}
                    </div>
                  )}
                </div>

                {/* Buy price */}
                <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 64 }}>
                  <div style={{ fontSize: '0.88rem', color: 'var(--text-dim)', marginBottom: 1 }}>Buy</div>
                  <div style={{ fontSize: '0.97rem', fontWeight: 600, color: 'var(--text)' }}>
                    {buy > 0 ? `$${buy.toFixed(2)}` : '—'}
                  </div>
                </div>

                {/* Arrow */}
                <div style={{ color: 'rgba(255,255,255,0.20)', fontSize: '1.01rem', flexShrink: 0 }}>→</div>

                {/* Sell price input */}
                <div style={{ flexShrink: 0 }}>
                  <div style={{ fontSize: '0.88rem', color: 'var(--text-dim)', marginBottom: 1 }}>Sell</div>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                      color: 'var(--text-dim)', fontSize: '0.96rem', pointerEvents: 'none' }}>$</span>
                    <input
                      type="number" min="0" step="0.01"
                      value={sellPrices[i]}
                      onChange={e => handleSell(i, e.target.value)}
                      placeholder="0.00"
                      style={{
                        width: 90, paddingLeft: 18, paddingRight: 8, paddingTop: 5, paddingBottom: 5,
                        borderRadius: 8,
                        border: hasSell
                          ? '1px solid rgba(74,222,128,0.35)'
                          : '1px solid rgba(255,255,255,0.14)',
                        background: hasSell ? 'rgba(74,222,128,0.04)' : 'rgba(255,255,255,0.05)',
                        color: '#fff', fontSize: '0.97rem',
                        fontFamily: 'var(--font-body)', outline: 'none',
                      }}
                    />
                  </div>
                </div>

                {/* Per-item P&L */}
                <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 72 }}>
                  <div style={{ fontSize: '0.88rem', color: 'var(--text-dim)', marginBottom: 1 }}>P&amp;L</div>
                  {profit !== null ? (
                    <div style={{ fontSize: '0.99rem', fontWeight: 700,
                      color: profit >= 0 ? profC : lossC }}>
                      {profit >= 0 ? '+' : ''}${profit.toFixed(2)}
                      {buy > 0 && (
                        <span style={{ fontSize: '0.88rem', opacity: 0.7, marginLeft: 3 }}>
                          ({((profit / buy) * 100).toFixed(1)}%)
                        </span>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.18)' }}>—</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Total summary bar ──────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap',
          padding: '14px 22px', background: 'rgba(255,255,255,0.03)',
          borderTop: '1px solid rgba(255,255,255,0.07)', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: '0.89rem', color: 'var(--text-dim)', marginBottom: 2 }}>Total Sell</div>
            <div style={{ fontSize: '1.09rem', fontWeight: 700, color: '#fff' }}>
              {totalSell > 0 ? `$${totalSell.toFixed(2)}` : '—'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.89rem', color: 'var(--text-dim)', marginBottom: 2 }}>Total P&amp;L</div>
            <div style={{ fontSize: '1.13rem', fontWeight: 700,
              color: totalSell > 0 ? (totalProfit >= 0 ? profC : lossC) : 'rgba(255,255,255,0.2)' }}>
              {totalSell > 0
                ? `${totalProfit >= 0 ? '+' : ''}$${totalProfit.toFixed(2)}`
                : '—'}
              {totalSell > 0 && roiPct !== null && (
                <span style={{ fontSize: '0.92rem', opacity: 0.75, marginLeft: 5 }}>
                  ({roiPct >= 0 ? '+' : ''}{roiPct.toFixed(1)}%)
                </span>
              )}
            </div>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
            <button onClick={onClose} style={{
              padding: '8px 20px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)',
              background: 'transparent', color: 'var(--text-muted)', fontSize: '0.99rem', cursor: 'pointer',
            }}>
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!allFilled}
              style={{
                padding: '8px 22px', borderRadius: 10, border: 'none',
                cursor: allFilled ? 'pointer' : 'not-allowed',
                background: allFilled ? 'var(--green)' : 'rgba(74,222,128,0.18)',
                color: allFilled ? '#000' : 'rgba(255,255,255,0.25)',
                fontSize: '0.99rem', fontWeight: 700,
                boxShadow: allFilled ? '0 2px 10px rgba(74,222,128,0.25)' : 'none',
              }}
            >
              Sell {items.length} item{items.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// Base-name, case-insensitive picture lookup — now one Map hit rather than a
// scan that lowercased and wear-stripped all 40,000 database names per card.
// The old version also had a branch for a per-condition image on `entry
// .conditions`; no entry in the database carries that field, so it never ran
// and the condition argument made no difference to the result.
const findSkinImg = findSkinImageLoose;
